<template>
  <div class="income-container">
    <Transition name="overlay-fade">
      <div
        v-if="isSaving"
        class="loading-overlay"
        role="status"
        aria-live="polite"
      >
        <div class="saving-card">
          <div class="saving-spinner"></div>

          <div class="saving-text">
            <p class="saving-title">
              Сохраняем ожидания
            </p>

            <p class="saving-subtitle">
              Это займёт всего несколько секунд
            </p>
          </div>
        </div>
      </div>
    </Transition>

    <div class="income-content">
      <header class="income-header">
        <div class="header-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M4 7.5C4 6.11929 5.11929 5 6.5 5H17.5C18.8807 5 20 6.11929 20 7.5V16.5C20 17.8807 18.8807 19 17.5 19H6.5C5.11929 19 4 17.8807 4 16.5V7.5Z"
              stroke="currentColor"
              stroke-width="1.8"
            />

            <path
              d="M4 9H20"
              stroke="currentColor"
              stroke-width="1.8"
            />

            <path
              d="M15.5 13H18"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />

            <path
              d="M8 3V6M16 3V6"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>
        </div>

        <div class="header-copy">
          <h2 class="income-title">
            Желаемый доход
          </h2>

          <p class="income-description">
            Укажите зарплатные ожидания, чтобы AI точнее подбирал
            подходящие вакансии. Этот шаг можно пропустить.
          </p>
        </div>
      </header>

      <section class="income-card">
        <div class="card-glow"></div>

        <div class="income-card-content">
          <div class="field-label-row">
            <div>
              <p class="field-label">
                Ожидаемый доход
              </p>

              <p class="field-caption">
                В месяц, до вычета налогов
              </p>
            </div>

            <span class="optional-badge">
              Необязательно
            </span>
          </div>

          <div
            class="salary-field"
            :class="{
              focused: isInputFocused,
              filled: hasSalary,
              invalid: salaryError
            }"
            @click="focusInput"
          >
            <span class="salary-prefix">
              {{ selectedCurrency.symbol }}
            </span>

            <input
              ref="salaryInputRef"
              v-model="salaryDisplay"
              type="text"
              inputmode="numeric"
              autocomplete="off"
              class="salary-input"
              placeholder="0"
              :disabled="isSaving"
              aria-label="Желаемый доход"
              @input="handleSalaryInput"
              @focus="isInputFocused = true"
              @blur="isInputFocused = false"
              @keydown.enter.prevent="handleEnter"
            />

            <button
              v-if="hasSalary"
              type="button"
              class="clear-salary-button"
              aria-label="Очистить сумму"
              @mousedown.prevent
              @click.stop="clearSalary"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M7 7L17 17M17 7L7 17"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </div>

          <Transition name="message-fade" mode="out-in">
            <p
              v-if="salaryError"
              key="error"
              class="field-message error"
            >
              {{ salaryError }}
            </p>

            <p
              v-else-if="salaryDescription"
              key="description"
              class="field-message"
            >
              {{ salaryDescription }}
            </p>

            <p
              v-else
              key="hint"
              class="field-message"
            >
              Например: 180 000
            </p>
          </Transition>

          <div class="currency-section">
            <div class="currency-heading">
              <div>
                <p class="currency-title">
                  Валюта
                </p>

                <p class="currency-caption">
                  Выберите валюту дохода
                </p>
              </div>
            </div>

            <div
              class="currency-segmented-control"
              role="radiogroup"
              aria-label="Валюта"
            >
              <button
                v-for="currency in currencies"
                :key="currency.code"
                type="button"
                class="currency-option"
                :class="{
                  selected: selectedCurrency.code === currency.code
                }"
                :aria-checked="selectedCurrency.code === currency.code"
                role="radio"
                :disabled="isSaving"
                @click="selectCurrency(currency)"
              >
                <span class="currency-symbol">
                  {{ currency.symbol }}
                </span>

                <span class="currency-name">
                  {{ currency.shortName }}
                </span>

                <span class="currency-check">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M5 12.5L9.2 16.5L19 7"
                      stroke="currentColor"
                      stroke-width="2.3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </div>

          <div class="income-note">
            <div class="note-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  stroke="currentColor"
                  stroke-width="1.8"
                />

                <path
                  d="M12 10.5V16"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />

                <circle
                  cx="12"
                  cy="7.5"
                  r="1"
                  fill="currentColor"
                />
              </svg>
            </div>

            <p>
              Работодатели будут видеть эту сумму как ваши текущие
              ожидания. Позже её можно изменить в профиле.
            </p>
          </div>
        </div>
      </section>

      <section class="suggestions-section">
        <div class="suggestions-header">
          <div>
            <p class="suggestions-title">
              Быстрый выбор
            </p>

            <p class="suggestions-description">
              Можно выбрать примерную сумму и затем скорректировать её
            </p>
          </div>
        </div>

        <div class="salary-suggestions">
          <button
            v-for="suggestion in currentSuggestions"
            :key="suggestion"
            type="button"
            class="salary-suggestion"
            :class="{ selected: numericSalary === suggestion }"
            :disabled="isSaving"
            @click="applySuggestion(suggestion)"
          >
            {{ formatNumber(suggestion) }}
            <span>{{ selectedCurrency.symbol }}</span>
          </button>
        </div>
      </section>

      <section
        v-if="!hasSalary"
        class="skip-card"
      >
        <div class="skip-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M5 12H19M14 7L19 12L14 17"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <div>
          <p class="skip-title">
            Пока не готовы назвать сумму?
          </p>

          <p class="skip-description">
            Оставьте поле пустым и переходите дальше. Это не повлияет
            на создание профиля.
          </p>
        </div>
      </section>
    </div>

    <ErrorToast
      v-if="errorMessage"
      :message="errorMessage"
    />

    <WarningToast
      v-if="warningMessage"
      :message="warningMessage"
    />

    <Snackbar
      message="Ожидания по доходу сохранены!"
      type="success"
      :show="successSnackbar"
    />
  </div>
</template>

<script setup>
import {
  computed,
  defineEmits,
  defineExpose,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from "vue";

import UserService from "../../api/UserService.js";

import ErrorToast from "../../components/ErrorToast.vue";
import WarningToast from "../../components/WarningToast.vue";
import Snackbar from "../../components/Snackbar.vue";

const emit = defineEmits(["save-salary"]);

const salaryInputRef = ref(null);

const salaryDisplay = ref("");
const numericSalary = ref(0);

const isInputFocused = ref(false);
const isSaving = ref(false);

const successSnackbar = ref(false);
const errorMessage = ref("");
const warningMessage = ref("");
const salaryError = ref("");

const currencies = [
  {
    symbol: "₽",
    code: "RUB",
    shortName: "Рубли",
    apiValue: 0,
    locale: "ru-RU",
  },
  {
    symbol: "$",
    code: "USD",
    shortName: "Доллары",
    apiValue: 1,
    locale: "en-US",
  },
  {
    symbol: "€",
    code: "EUR",
    shortName: "Евро",
    apiValue: 2,
    locale: "de-DE",
  },
];

const selectedCurrency = ref(currencies[0]);

let successTimer = null;
let errorTimer = null;
let warningTimer = null;

const hasSalary = computed(() => {
  return numericSalary.value > 0;
});

const currentSuggestions = computed(() => {
  switch (selectedCurrency.value.code) {
    case "USD":
      return [1000, 2000, 3000, 5000];

    case "EUR":
      return [1000, 2000, 3000, 5000];

    default:
      return [100000, 150000, 200000, 300000];
  }
});

const salaryDescription = computed(() => {
  if (!hasSalary.value) {
    return "";
  }

  return describeSalary(
    numericSalary.value,
    selectedCurrency.value
  );
});

const formatNumber = (value) => {
  if (!value) {
    return "";
  }

  return new Intl.NumberFormat("ru-RU", {
    maximumFractionDigits: 0,
  }).format(value);
};

const handleSalaryInput = (event) => {
  salaryError.value = "";

  const rawValue = String(event.target.value || "");
  const digits = rawValue.replace(/\D/g, "");

  if (!digits) {
    numericSalary.value = 0;
    salaryDisplay.value = "";
    return;
  }

  /*
   Ограничиваем длину, чтобы пользователь случайно
   не вставил огромную сумму.
  */
  const limitedDigits = digits.slice(0, 12);
  const parsedValue = Number(limitedDigits);

  numericSalary.value = Number.isFinite(parsedValue)
    ? parsedValue
    : 0;

  salaryDisplay.value = formatNumber(numericSalary.value);
};

const applySuggestion = (value) => {
  salaryError.value = "";
  numericSalary.value = value;
  salaryDisplay.value = formatNumber(value);

  focusInput();
};

const clearSalary = () => {
  salaryDisplay.value = "";
  numericSalary.value = 0;
  salaryError.value = "";

  focusInput();
};

const selectCurrency = (currency) => {
  selectedCurrency.value = currency;
  salaryError.value = "";
};

const focusInput = async () => {
  if (isSaving.value) {
    return;
  }

  await nextTick();
  salaryInputRef.value?.focus();
};

const describeSalary = (value, currency) => {
  const formattedValue = formatNumber(value);

  if (currency.code === "RUB") {
    if (value >= 1_000_000) {
      const millions = value / 1_000_000;

      const formattedMillions = new Intl.NumberFormat("ru-RU", {
        maximumFractionDigits: 1,
      }).format(millions);

      return `≈ ${formattedMillions} млн ₽ в месяц`;
    }

    if (value >= 1000) {
      const thousands = Math.round(value / 1000);

      return `≈ ${thousands} тыс. ₽ в месяц`;
    }

    return `≈ ${formattedValue} ₽ в месяц`;
  }

  return `≈ ${formattedValue} ${currency.symbol} в месяц`;
};

const validateSalary = () => {
  salaryError.value = "";

  if (!hasSalary.value) {
    return true;
  }

  if (numericSalary.value < 1) {
    salaryError.value = "Введите корректную сумму.";
    return false;
  }

  if (numericSalary.value > 999_999_999_999) {
    salaryError.value = "Указана слишком большая сумма.";
    return false;
  }

  return true;
};

const clearMessages = () => {
  errorMessage.value = "";
  warningMessage.value = "";
};

const showSuccess = () => {
  window.clearTimeout(successTimer);

  successSnackbar.value = false;

  nextTick(() => {
    successSnackbar.value = true;
  });

  successTimer = window.setTimeout(() => {
    successSnackbar.value = false;
  }, 2600);
};

const showError = (message) => {
  window.clearTimeout(errorTimer);

  errorMessage.value = "";

  nextTick(() => {
    errorMessage.value = message;
  });

  errorTimer = window.setTimeout(() => {
    errorMessage.value = "";
  }, 4500);
};

const showWarning = (message) => {
  window.clearTimeout(warningTimer);

  warningMessage.value = "";

  nextTick(() => {
    warningMessage.value = message;
  });

  warningTimer = window.setTimeout(() => {
    warningMessage.value = "";
  }, 3500);
};

const saveSalary = async () => {
  if (isSaving.value) {
    return false;
  }

  clearMessages();

  if (!validateSalary()) {
    focusInput();
    return false;
  }

  /*
   Поле необязательное. Если сумма не указана,
   просто разрешаем регистрации перейти дальше.
  */
  if (!hasSalary.value) {
    emit("save-salary", {
      salary: null,
      currency: selectedCurrency.value.apiValue,
      skipped: true,
    });

    return true;
  }

  isSaving.value = true;

  try {
    await UserService.updateUserSalary(
      numericSalary.value.toString(),
      selectedCurrency.value.apiValue
    );

    showSuccess();

    emit("save-salary", {
      salary: numericSalary.value,
      currency: selectedCurrency.value.apiValue,
      currencyCode: selectedCurrency.value.code,
      skipped: false,
    });

    console.log(
      "✅ Желаемый доход пользователя успешно обновлён:",
      numericSalary.value,
      selectedCurrency.value.code
    );

    return true;
  } catch (error) {
    console.error(
      "❌ Ошибка при обновлении желаемого дохода:",
      error?.response?.data || error?.message || error
    );

    showError(
      "Не удалось сохранить желаемый доход. Попробуйте позже."
    );

    return false;
  } finally {
    isSaving.value = false;
  }
};

const updateSalary = () => {
  saveSalary();
};

const handleEnter = () => {
  salaryInputRef.value?.blur();
};

watch(selectedCurrency, () => {
  /*
   Если валюта изменилась, текущую сумму сохраняем.
   Пользователь сам решает, нужна ли корректировка.
  */
  salaryError.value = "";
});

watch(isSaving, (value) => {
  document.body.classList.toggle(
    "income-saving-lock",
    value
  );
});

onMounted(() => {
  document.addEventListener(
    "save-salary",
    updateSalary
  );

  console.log("🟢 Salary подписан на save-salary");
});

onUnmounted(() => {
  document.removeEventListener(
    "save-salary",
    updateSalary
  );

  window.clearTimeout(successTimer);
  window.clearTimeout(errorTimer);
  window.clearTimeout(warningTimer);

  document.body.classList.remove(
    "income-saving-lock"
  );

  console.log("🔴 Salary отписан от save-salary");
});

defineExpose({
  saveSalary,
  updateSalary,
  salary: numericSalary,
  selectedCurrency,
});
</script>

<style scoped>
.income-container {
  --accent: #2887f7;
  --accent-hover: #1978e8;
  --accent-soft: rgba(40, 135, 247, 0.11);
  --accent-border: rgba(40, 135, 247, 0.3);

  --surface-primary: rgba(255, 255, 255, 0.055);
  --surface-secondary: rgba(255, 255, 255, 0.035);
  --surface-hover: rgba(255, 255, 255, 0.08);

  --border-primary: rgba(255, 255, 255, 0.1);
  --border-secondary: rgba(255, 255, 255, 0.065);

  --text-primary: rgba(255, 255, 255, 0.96);
  --text-secondary: rgba(255, 255, 255, 0.62);
  --text-tertiary: rgba(255, 255, 255, 0.4);

  width: 100%;
  min-height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
}

[data-theme="light"] .income-container {
  --surface-primary: #ffffff;
  --surface-secondary: #f6f8fb;
  --surface-hover: #f1f5fa;

  --border-primary: rgba(26, 43, 68, 0.11);
  --border-secondary: rgba(26, 43, 68, 0.07);

  --text-primary: #172033;
  --text-secondary: #657086;
  --text-tertiary: #929caf;
}

.income-content {
  width: 100%;
  max-width: 760px;
  display: flex;
  flex-direction: column;
}

/* Header */

.income-header {
  display: flex;
  align-items: flex-start;
  gap: 18px;
  margin-bottom: 28px;
}

.header-icon {
  width: 54px;
  height: 54px;
  flex: 0 0 54px;
  display: flex;
  align-items: center;
  justify-content: center;

  color: var(--accent);

  background:
    linear-gradient(
      145deg,
      rgba(40, 135, 247, 0.18),
      rgba(40, 135, 247, 0.06)
    );

  border: 1px solid rgba(40, 135, 247, 0.22);
  border-radius: 17px;

  box-shadow:
    0 12px 30px rgba(40, 135, 247, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
}

.header-icon svg {
  width: 28px;
  height: 28px;
}

.header-copy {
  min-width: 0;
}

.income-title {
  margin: 1px 0 7px;

  color: var(--text-primary);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 26px;
  line-height: 1.2;
  letter-spacing: -0.45px;
}

.income-description {
  max-width: 610px;
  margin: 0;

  color: var(--text-secondary);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  line-height: 1.55;
}

/* Main card */

.income-card {
  width: 100%;
  position: relative;
  overflow: hidden;

  background:
    linear-gradient(
      145deg,
      var(--surface-primary),
      var(--surface-secondary)
    );

  border: 1px solid var(--border-primary);
  border-radius: 25px;

  box-shadow:
    0 18px 45px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.035);
}

.card-glow {
  width: 270px;
  height: 270px;
  position: absolute;
  top: -150px;
  right: -100px;

  background:
    radial-gradient(
      circle,
      rgba(40, 135, 247, 0.19),
      transparent 68%
    );

  pointer-events: none;
}

.income-card-content {
  position: relative;
  z-index: 1;
  padding: 25px;
}

.field-label-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 17px;
}

.field-label {
  margin: 0 0 4px;

  color: var(--text-primary);

  font-family: "Eastman-Medium", sans-serif;
  font-size: 15px;
}

.field-caption {
  margin: 0;

  color: var(--text-tertiary);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
}

.optional-badge {
  flex: 0 0 auto;
  padding: 6px 9px;

  color: var(--text-secondary);
  background: var(--surface-secondary);

  border: 1px solid var(--border-secondary);
  border-radius: 9px;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 10px;
}

/* Salary field */

.salary-field {
  min-height: 92px;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 21px;

  color: var(--text-tertiary);
  background: rgba(0, 0, 0, 0.07);

  border: 1px solid var(--border-primary);
  border-radius: 20px;

  cursor: text;

  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;
}

[data-theme="light"] .salary-field {
  background: #f7f9fc;
}

.salary-field:hover {
  border-color: rgba(40, 135, 247, 0.22);
}

.salary-field.focused {
  background: var(--surface-primary);
  border-color: rgba(40, 135, 247, 0.72);

  box-shadow:
    0 0 0 4px rgba(40, 135, 247, 0.1),
    0 12px 30px rgba(0, 0, 0, 0.08);
}

.salary-field.invalid {
  border-color: rgba(235, 87, 87, 0.7);

  box-shadow:
    0 0 0 4px rgba(235, 87, 87, 0.09);
}

.salary-prefix {
  min-width: 31px;

  color: var(--accent);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 31px;
  line-height: 1;
}

.salary-input {
  width: 100%;
  min-width: 0;
  flex: 1;
  padding: 0;

  color: var(--text-primary);
  background: transparent;

  border: none;
  outline: none;

  caret-color: var(--accent);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 37px;
  line-height: 1;
  letter-spacing: -0.8px;
}

.salary-input::placeholder {
  color: var(--text-tertiary);
  opacity: 0.55;
}

.salary-input:disabled {
  cursor: not-allowed;
}

.clear-salary-button {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;

  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  color: var(--text-secondary);
  background: var(--surface-secondary);

  border: 1px solid var(--border-secondary);
  border-radius: 50%;

  cursor: pointer;

  transition:
    color 0.18s ease,
    background 0.18s ease,
    transform 0.18s ease;
}

.clear-salary-button:hover {
  color: var(--text-primary);
  background: var(--surface-hover);
  transform: scale(1.05);
}

.clear-salary-button svg {
  width: 16px;
  height: 16px;
}

.field-message {
  min-height: 18px;
  margin: 9px 3px 0;

  color: var(--text-tertiary);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
  line-height: 1.4;
}

.field-message.error {
  color: #eb5757;
}

/* Currency */

.currency-section {
  margin-top: 24px;
}

.currency-heading {
  margin-bottom: 12px;
}

.currency-title {
  margin: 0 0 4px;

  color: var(--text-primary);

  font-family: "Eastman-Medium", sans-serif;
  font-size: 14px;
}

.currency-caption {
  margin: 0;

  color: var(--text-tertiary);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
}

.currency-segmented-control {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 9px;
}

.currency-option {
  min-height: 66px;
  position: relative;

  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 13px;

  color: var(--text-primary);
  background: var(--surface-secondary);

  border: 1px solid var(--border-secondary);
  border-radius: 16px;

  cursor: pointer;
  text-align: left;

  transition:
    background 0.18s ease,
    border-color 0.18s ease,
    transform 0.18s ease,
    box-shadow 0.18s ease;
}

.currency-option:hover:not(:disabled) {
  background: var(--surface-hover);
  border-color: rgba(40, 135, 247, 0.22);
  transform: translateY(-1px);
}

.currency-option.selected {
  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.15),
      rgba(40, 135, 247, 0.055)
    );

  border-color: var(--accent-border);

  box-shadow:
    0 8px 20px rgba(40, 135, 247, 0.09);
}

.currency-option:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.currency-symbol {
  width: 37px;
  height: 37px;
  flex: 0 0 37px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: var(--text-secondary);
  background: var(--surface-primary);

  border: 1px solid var(--border-primary);
  border-radius: 11px;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 18px;

  transition:
    color 0.18s ease,
    background 0.18s ease,
    border-color 0.18s ease;
}

.currency-option.selected .currency-symbol {
  color: #ffffff;
  background: var(--accent);
  border-color: var(--accent);

  box-shadow:
    0 6px 15px rgba(40, 135, 247, 0.23);
}

.currency-name {
  min-width: 0;
  flex: 1;

  overflow: hidden;

  color: var(--text-secondary);

  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;

  white-space: nowrap;
  text-overflow: ellipsis;
}

.currency-option.selected .currency-name {
  color: var(--text-primary);
}

.currency-check {
  width: 21px;
  height: 21px;
  flex: 0 0 21px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: transparent;
  background: transparent;

  border: 1px solid var(--border-primary);
  border-radius: 50%;

  transition:
    color 0.18s ease,
    background 0.18s ease,
    border-color 0.18s ease;
}

.currency-option.selected .currency-check {
  color: #ffffff;
  background: var(--accent);
  border-color: var(--accent);
}

.currency-check svg {
  width: 12px;
  height: 12px;
}

/* Note */

.income-note {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  margin-top: 21px;
  padding: 13px 14px;

  background: var(--accent-soft);

  border: 1px solid rgba(40, 135, 247, 0.17);
  border-radius: 14px;
}

.note-icon {
  width: 24px;
  height: 24px;
  flex: 0 0 24px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: var(--accent);
}

.note-icon svg {
  width: 20px;
  height: 20px;
}

.income-note p {
  margin: 1px 0 0;

  color: var(--text-secondary);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
  line-height: 1.5;
}

/* Suggestions */

.suggestions-section {
  margin-top: 21px;
}

.suggestions-header {
  margin-bottom: 12px;
}

.suggestions-title {
  margin: 0 0 4px;

  color: var(--text-primary);

  font-family: "Eastman-Medium", sans-serif;
  font-size: 14px;
}

.suggestions-description {
  margin: 0;

  color: var(--text-tertiary);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
}

.salary-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
}

.salary-suggestion {
  min-height: 39px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 13px;

  color: var(--text-secondary);
  background: var(--surface-secondary);

  border: 1px solid var(--border-secondary);
  border-radius: 12px;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;

  cursor: pointer;

  transition:
    color 0.18s ease,
    background 0.18s ease,
    border-color 0.18s ease,
    transform 0.18s ease;
}

.salary-suggestion span {
  color: var(--text-tertiary);
}

.salary-suggestion:hover:not(:disabled) {
  color: var(--accent);
  background: var(--accent-soft);
  border-color: var(--accent-border);
  transform: translateY(-1px);
}

.salary-suggestion.selected {
  color: #ffffff;
  background: var(--accent);
  border-color: var(--accent);

  box-shadow:
    0 7px 17px rgba(40, 135, 247, 0.2);
}

.salary-suggestion.selected span {
  color: rgba(255, 255, 255, 0.8);
}

.salary-suggestion:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Skip card */

.skip-card {
  display: flex;
  align-items: flex-start;
  gap: 13px;
  margin-top: 18px;
  padding: 15px 16px;

  background: var(--surface-secondary);

  border: 1px dashed var(--border-primary);
  border-radius: 16px;
}

.skip-icon {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: var(--text-secondary);
  background: var(--surface-primary);

  border: 1px solid var(--border-secondary);
  border-radius: 11px;
}

.skip-icon svg {
  width: 18px;
  height: 18px;
}

.skip-title {
  margin: 0 0 4px;

  color: var(--text-primary);

  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;
}

.skip-description {
  margin: 0;

  color: var(--text-tertiary);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
  line-height: 1.5;
}

/* Saving overlay */

.loading-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;

  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;

  background: rgba(7, 12, 22, 0.58);

  backdrop-filter: blur(9px);
  -webkit-backdrop-filter: blur(9px);
}

.saving-card {
  min-width: 320px;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 21px 23px;

  background: rgba(27, 34, 47, 0.96);

  border: 1px solid rgba(255, 255, 255, 0.11);
  border-radius: 21px;

  box-shadow:
    0 26px 70px rgba(0, 0, 0, 0.32);
}

[data-theme="light"] .saving-card {
  background: rgba(255, 255, 255, 0.97);
  border-color: rgba(20, 38, 65, 0.1);
}

.saving-spinner {
  width: 43px;
  height: 43px;
  flex: 0 0 43px;

  border: 4px solid rgba(40, 135, 247, 0.2);
  border-top-color: var(--accent);
  border-radius: 50%;

  animation: spin 0.75s linear infinite;
}

.saving-text {
  min-width: 0;
}

.saving-title {
  margin: 0 0 4px;

  color: var(--text-primary);

  font-family: "Eastman-Medium", sans-serif;
  font-size: 15px;
}

.saving-subtitle {
  margin: 0;

  color: var(--text-secondary);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
}

/* Transitions */

.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.2s ease;
}

.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

.message-fade-enter-active,
.message-fade-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.message-fade-enter-from {
  opacity: 0;
  transform: translateY(3px);
}

.message-fade-leave-to {
  opacity: 0;
  transform: translateY(-3px);
}

/* Animations */

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Tablet and mobile */

@media (max-width: 768px) {
  .income-content {
    max-width: none;
  }

  .income-header {
    gap: 13px;
    margin-bottom: 23px;
  }

  .header-icon {
    width: 46px;
    height: 46px;
    flex-basis: 46px;
    border-radius: 15px;
  }

  .header-icon svg {
    width: 24px;
    height: 24px;
  }

  .income-title {
    margin-top: 0;
    font-size: 21px;
    letter-spacing: -0.3px;
  }

  .income-description {
    font-size: 13px;
    line-height: 1.48;
  }

  .income-card {
    border-radius: 21px;
  }

  .income-card-content {
    padding: 19px;
  }

  .salary-field {
    min-height: 82px;
    padding: 0 17px;
    border-radius: 18px;
  }

  .salary-prefix {
    min-width: 27px;
    font-size: 27px;
  }

  .salary-input {
    font-size: 31px;
  }

  .currency-segmented-control {
    gap: 7px;
  }

  .currency-option {
    min-height: 62px;
    padding: 9px 10px;
    border-radius: 14px;
  }

  .currency-symbol {
    width: 34px;
    height: 34px;
    flex-basis: 34px;
    font-size: 16px;
  }

  .currency-name {
    font-size: 11px;
  }

  .currency-check {
    display: none;
  }

  .saving-card {
    width: 100%;
    min-width: 0;
    max-width: 360px;
  }
}

@media (max-width: 520px) {
  .field-label-row {
    gap: 10px;
  }

  .income-card-content {
    padding: 16px;
  }

  .salary-field {
    min-height: 76px;
    gap: 10px;
    padding: 0 14px;
  }

  .salary-prefix {
    min-width: 24px;
    font-size: 24px;
  }

  .salary-input {
    font-size: 28px;
    letter-spacing: -0.5px;
  }

  .currency-segmented-control {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .currency-option {
    flex-direction: column;
    justify-content: center;
    gap: 5px;
    padding: 8px 5px;
    text-align: center;
  }

  .currency-symbol {
    width: 32px;
    height: 32px;
    flex-basis: 32px;
  }

  .currency-name {
    width: 100%;
    font-size: 9px;
  }

  .salary-suggestions {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
  }

  .salary-suggestion {
    justify-content: center;
  }
}

@media (max-width: 420px) {
  .income-header {
    align-items: center;
  }

  .income-description {
    display: none;
  }

  .income-title {
    font-size: 20px;
  }

  .optional-badge {
    display: none;
  }

  .field-label-row {
    margin-bottom: 13px;
  }

  .salary-field {
    min-height: 72px;
  }

  .salary-input {
    font-size: 26px;
  }

  .income-note {
    padding: 12px;
  }

  .skip-card {
    padding: 13px;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>

<style>
body.income-saving-lock {
  overflow: hidden;
}
</style>